import {
  CONSENT_FORM_TEMPLATES,
  getPracticeConsentFormsCollectionPath,
  getTemplateCollectionPath,
  ShortcutArgs,
  Template,
  UpdateCallback,
} from '@hb/shared'
import { collection, doc, setDoc, updateDoc } from 'firebase/firestore'
import { PropsWithChildren, useCallback, useMemo } from 'react'

import { addMetadata, useApp } from '@hb/react'
import { db } from '../../../backend/db/db'
import { useDocument } from '../../../backend/db/hooks/useDocument'
import { TemplateEditorProvider } from './editor'
import { TemplatePracticeDataProvider, useTemplatePractice } from './practice'

const ConsentFormTemplateProviderBody = ({
  children,
  templateId,
  templatePracticeId,
}: PropsWithChildren<{ templateId: string; templatePracticeId: string | null }>) => {
  const data = useTemplatePractice()

  const { practice, isLoading: practiceLoading } = data
  const consentFormTemplatesCollection = useMemo(
    () =>
      templatePracticeId
        ? getPracticeConsentFormsCollectionPath(templatePracticeId)
        : CONSENT_FORM_TEMPLATES,
    [templatePracticeId],
  )

  const { appName } = useApp()
  const { data: template, loading: templateLoading } = useDocument<Template>(
    consentFormTemplatesCollection,
    templateId,
  )
  const isLoading = useMemo(
    () => templateLoading || practiceLoading,
    [templateLoading, practiceLoading],
  )

  const onSubmit = useCallback(
    async (updated: Omit<Template, 'rank'>): Promise<UpdateCallback> => {
      if (!consentFormTemplatesCollection) {
        throw new Error('No practice selected')
      }
      const consentFormsRef = collection(
        db,
        getTemplateCollectionPath(templatePracticeId, 'consentForm'),
      )
      const submitted = addMetadata(updated, appName, false)
      try {
        await setDoc(doc(consentFormsRef, templateId), submitted, {
          merge: true,
        })
        return { success: 'Updated template!' }
      } catch (err: any) {
        console.error(err)
        return { error: err?.message || 'Error saving template' }
      }
    },
    [templatePracticeId, appName, templateId, consentFormTemplatesCollection],
  )

  const onUpdateField = useCallback(
    async (field: string, value: any) => {
      if (!consentFormTemplatesCollection) {
        throw new Error('No practice selected')
      }
      try {
        const consentFormsRef = collection(db, consentFormTemplatesCollection)
        await updateDoc(doc(consentFormsRef, templateId), field, value)
        return { success: 'Updated template' }
      } catch (err: any) {
        console.error(err)
        return { error: err?.message || 'Error saving template' }
      }
    },
    [consentFormTemplatesCollection, templateId],
  )

  const shortcutArgs = useMemo<ShortcutArgs>(() => ({ practice, assessmentId: null }), [practice])
  return (
    <TemplateEditorProvider
      isLoading={isLoading}
      onSubmit={onSubmit}
      onUpdateField={onUpdateField}
      template={template}
      type="consentForm"
      templatePracticeId={templatePracticeId}
      shortcutArgs={shortcutArgs}>
      {children}
    </TemplateEditorProvider>
  )
}

export const ConsentFormTemplateProvider = ({
  children,
  templateId,
  templatePracticeId,
}: PropsWithChildren<{ templateId: string; templatePracticeId: string | null }>) => (
  <TemplatePracticeDataProvider templatePracticeId={templatePracticeId}>
    <ConsentFormTemplateProviderBody
      templateId={templateId}
      templatePracticeId={templatePracticeId}>
      {children}
    </ConsentFormTemplateProviderBody>
  </TemplatePracticeDataProvider>
)
